'use client';

import React, { useState } from 'react';
import Image from 'next/image';

export interface CategoryIconProps {
  category?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'badge' | 'raw' | 'minimal' | 'circle';
  isActive?: boolean;
  className?: string;
  imgClassName?: string;
  alt?: string;
  showName?: boolean;
}

const CATEGORY_MAP: Record<string, string> = {
  // Braking
  braking: 'braking.webp',
  brakes: 'braking.webp',
  'braking-system': 'braking.webp',
  'braking system': 'braking.webp',
  brk: 'braking.webp',

  // Suspension
  suspension: 'suspension.webp',
  'suspension-system': 'suspension.webp',
  'suspension system': 'suspension.webp',
  'suspension-steering': 'suspension.webp',
  'suspension & steering': 'suspension.webp',
  sus: 'suspension.webp',

  // Steering
  steering: 'steering.webp',
  'steering-system': 'steering.webp',
  'steering system': 'steering.webp',
  'power-steering': 'steering.webp',
  str: 'steering.webp',

  // Engine
  engine: 'engine.webp',
  'engine-components': 'engine.webp',
  'engine components': 'engine.webp',
  'engine-mechanical': 'engine.webp',
  'engine & mechanical': 'engine.webp',
  'engine & valvetrain': 'engine.webp',
  mechanical: 'engine.webp',
  eng: 'engine.webp',

  // Filtration
  filtration: 'filtration.webp',
  filters: 'filtration.webp',
  'fluids-filters': 'filtration.webp',
  'filters & service': 'filtration.webp',
  'filters-service': 'filtration.webp',
  'filtration & fluids': 'filtration.webp',
  flt: 'filtration.webp',

  // Ignition & Electrical
  ignition: 'ignition.webp',
  'ignition-system': 'ignition.webp',
  'ignition & electrical': 'ignition.webp',
  'ignition-electrical': 'ignition.webp',
  ign: 'ignition.webp',

  // Electrical
  electrical: 'electrical.webp',
  'electrical-lighting': 'electrical.webp',
  'electrical & lighting': 'electrical.webp',
  'electrical & electronics': 'electrical.webp',
  'electrical-electronics': 'electrical.webp',
  ele: 'electrical.webp',

  // Cooling & Radiators
  cooling: 'cooling.webp',
  'cooling-heating': 'cooling.webp',
  'cooling-system': 'cooling.webp',
  'cooling & ac system': 'cooling.webp',
  'cooling-climate': 'cooling.webp',
  clg: 'cooling.webp',

  // Heating & AC (HVAC)
  hvac: 'hvac.webp',
  ac: 'hvac.webp',
  'heating-ac': 'hvac.webp',
  'heating & ac': 'hvac.webp',
  'air-conditioning': 'hvac.webp',
  'climate-control': 'hvac.webp',

  // Transmission & Drivetrain
  transmission: 'transmission.webp',
  drivetrain: 'transmission.webp',
  'transmission-axles': 'transmission.webp',
  'transmission-drivetrain': 'transmission.webp',
  'transmission & drivetrain': 'transmission.webp',
  'drivetrain & gearbox': 'transmission.webp',
  gearbox: 'transmission.webp',
  trn: 'transmission.webp',

  // Lighting
  lighting: 'lighting.webp',
  'lighting-visibility': 'lighting.webp',
  'lighting & visibility': 'lighting.webp',
  lgt: 'lighting.webp',

  // Body & Panels
  body: 'body.webp',
  'body-parts': 'body.webp',
  'body & panels': 'body.webp',
  'body, trim & panels': 'body.webp',
  'body, trim & mirrors': 'body.webp',
  'body-trim-panels': 'body.webp',
  panels: 'body.webp',
  bdy: 'body.webp',

  // Fuel System
  fuel: 'fuel.webp',
  'fuel-system': 'fuel.webp',
  'fuel system': 'fuel.webp',
  'fuel system & delivery': 'fuel.webp',
  fue: 'fuel.webp',

  // Exhaust & Emissions
  exhaust: 'exhaust.webp',
  'exhaust-emissions': 'exhaust.webp',
  'exhaust & emissions': 'exhaust.webp',
  emissions: 'exhaust.webp',
  exh: 'exhaust.webp',

  // Wheels, Hubs & Tires
  wheels: 'wheels.webp',
  tires: 'wheels.webp',
  'wheels-tires': 'wheels.webp',
  'wheels & tires': 'wheels.webp',
  'wheels, hubs & bearings': 'wheels.webp',
  whl: 'wheels.webp',

  // Interior & Cabin
  interior: 'interior.webp',
  cabin: 'interior.webp',
  'interior-accessories': 'interior.webp',
  'interior & cabin': 'interior.webp',
  'cabin & interior': 'interior.webp',
  int: 'interior.webp',

  // Hardware, Clips & Fasteners
  hardware: 'hardware.webp',
  fasteners: 'hardware.webp',
  clips: 'hardware.webp',
  'hardware & clips': 'hardware.webp',
  'hardware-clips': 'hardware.webp',
  hdw: 'hardware.webp',

  // EV Components & High Voltage
  ev: 'ev.webp',
  'ev-components': 'ev.webp',
  'ev components': 'ev.webp',
  electric: 'ev.webp',
  hybrid: 'ev.webp',

  // ADAS & Sensors
  adas: 'adas.webp',
  sensors: 'adas.webp',
  'adas-sensors': 'adas.webp',
  'adas & sensors': 'adas.webp',

  // General Maintenance & Fluids
  general: 'general.webp',
  fluids: 'general.webp',
  maintenance: 'general.webp',
  'fluids-maintenance': 'general.webp',
  'fluids & maintenance': 'general.webp',
};

const SIZE_CONFIGS = {
  xs: {
    badge: 'size-6 p-0.5 rounded-lg',
    img: 'size-4',
    text: 'text-[9px]',
    px: 16,
  },
  sm: {
    badge: 'size-8 p-1 rounded-xl',
    img: 'size-6',
    text: 'text-[11px]',
    px: 24,
  },
  md: {
    badge: 'size-10 p-1.5 rounded-xl',
    img: 'size-8',
    text: 'text-xs',
    px: 32,
  },
  lg: {
    badge: 'size-14 p-2 rounded-2xl',
    img: 'size-11',
    text: 'text-sm',
    px: 44,
  },
  xl: {
    badge: 'size-20 p-2.5 rounded-3xl',
    img: 'size-16',
    text: 'text-base',
    px: 64,
  },
  circle: {
    badge: 'size-16 sm:size-18 lg:size-20 p-1.5 sm:p-2 rounded-full',
    img: 'w-full h-full',
    text: 'text-xs',
    px: 64,
  },
};

export default function CategoryIcon({
  category,
  size = 'md',
  variant = 'badge',
  isActive = false,
  className = '',
  imgClassName = '',
  alt,
  showName = false,
}: CategoryIconProps) {
  const [formatIndex, setFormatIndex] = useState(0); // 0: webp, 1: png, 2: svg, 3: error
  const formats = ['webp', 'png', 'svg'];

  const cleanKey = (category || '').toLowerCase().trim();
  let baseName = 'general';
  if (CATEGORY_MAP[cleanKey]) {
    baseName = CATEGORY_MAP[cleanKey].replace(/\.[^.]+$/, '');
  } else if (/brake|braking/i.test(cleanKey)) baseName = 'braking';
  else if (/engine|motor|piston|valve|cylinder|mechanical/i.test(cleanKey)) baseName = 'engine';
  else if (/suspen|shock|strut|spring|control arm/i.test(cleanKey)) baseName = 'suspension';
  else if (/steer|rack|pinion/i.test(cleanKey)) baseName = 'steering';
  else if (/light|headlight|lamp|bulb|visibility/i.test(cleanKey)) baseName = 'lighting';
  else if (/transm|gearbox|clutch|drivetrain|axle/i.test(cleanKey)) baseName = 'transmission';
  else if (/body|bumper|grille|fender|door|hood|panel|mirror/i.test(cleanKey)) baseName = 'body';
  else if (/cool|radiator|fan|thermostat/i.test(cleanKey)) baseName = 'cooling';
  else if (/hvac|ac|air condition|heat|climate/i.test(cleanKey)) baseName = 'hvac';
  else if (/filter|fluid|oil filter|air filter/i.test(cleanKey)) baseName = 'filtration';
  else if (/electr|battery|starter|alternator/i.test(cleanKey)) baseName = 'electrical';
  else if (/spark|ignition|coil/i.test(cleanKey)) baseName = 'ignition';
  else if (/fuel|injector|pump|gas/i.test(cleanKey)) baseName = 'fuel';
  else if (/exhaust|muffler|catalytic|emission/i.test(cleanKey)) baseName = 'exhaust';
  else if (/wheel|tire|tyre|rim|hub/i.test(cleanKey)) baseName = 'wheels';
  else if (/interior|seat|dashboard|cabin|mat/i.test(cleanKey)) baseName = 'interior';
  else if (/adas|sensor|camera|radar/i.test(cleanKey)) baseName = 'adas';
  else if (/ev|electric|hybrid|voltage/i.test(cleanKey)) baseName = 'ev';
  else if (/hardware|fastener|clip|bolt|nut/i.test(cleanKey)) baseName = 'hardware';
  else baseName = 'general';

  const ext = formats[formatIndex] || 'webp';
  const iconSrc = `/icons/categories/${baseName}.${ext}`;
  const config = SIZE_CONFIGS[size] || SIZE_CONFIGS.md;
  const label = alt || category || 'Auto Part Category';

  const handleImgError = () => {
    if (formatIndex < formats.length - 1) {
      setFormatIndex(prev => prev + 1);
    } else {
      setFormatIndex(formats.length); // Error state
    }
  };

  const hasError = formatIndex >= formats.length;

  // Variant: Circle (dedicated for carousel circles & circular category pickers)
  if (variant === 'circle') {
    return (
      <div
        className={`size-16 sm:size-18 lg:size-20 rounded-full flex items-center justify-center p-3 sm:p-3.5 transition-all duration-200 shrink-0 select-none ${
          isActive
            ? 'bg-blue-50 dark:bg-blue-950/40 border-2 border-blue-500 ring-4 ring-blue-500/25 shadow-md scale-105'
            : 'bg-white dark:bg-[#0B1528] border border-slate-200/90 dark:border-slate-800/90 shadow-xs group-hover:scale-105 group-hover:border-blue-500 group-hover:shadow-md'
        } ${className}`}
      >
        {!hasError ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={iconSrc}
            alt={label}
            width={48}
            height={48}
            className={`w-full h-full object-contain transition-transform duration-200 group-hover:scale-110 ${imgClassName}`}
            onError={handleImgError}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#0A1B4A] font-extrabold text-base">
            ⚙
          </div>
        )}
      </div>
    );
  }

  // Variant: Raw (direct image without badge padding)
  if (variant === 'raw') {
    return (
      <div className={`inline-flex items-center justify-center select-none ${className}`}>
        {!hasError ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={iconSrc}
            alt={label}
            width={config.px}
            height={config.px}
            className={`${imgClassName || config.img} object-contain`}
            onError={handleImgError}
            loading="lazy"
          />
        ) : (
          <div className={`${imgClassName || config.img} flex items-center justify-center text-[#0A1B4A] font-extrabold text-[10px]`}>
            ⚙
          </div>
        )}
      </div>
    );
  }

  const badgeWrapperClasses = variant === 'badge'
    ? `${config.badge} bg-white border border-slate-200/90 shadow-xs flex items-center justify-center shrink-0 transition-transform group-hover:scale-105`
    : 'flex items-center justify-center shrink-0';

  return (
    <div className={`inline-flex items-center justify-center select-none ${showName ? 'gap-2' : ''} ${className}`}>
      <div className={badgeWrapperClasses}>
        {!hasError ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={iconSrc}
            alt={label}
            width={config.px}
            height={config.px}
            className={`${imgClassName || config.img} object-contain transition-all`}
            onError={handleImgError}
            loading="lazy"
          />
        ) : (
          <div className={`${config.img} flex items-center justify-center text-[#0A1B4A] font-extrabold text-[10px]`}>
            ⚙
          </div>
        )}
      </div>

      {showName && category && (
        <span className={`font-semibold text-[#0A1B4A] dark:text-white capitalize ${config.text}`}>
          {category}
        </span>
      )}
    </div>
  );
}
